import React, {useContext, useState} from "react";
import { Table, Tag, Popover, Space, Modal, Button} from "antd";
import moment from "moment";
import { SearchOutlined, TeamOutlined} from "@ant-design/icons";
import { useListarSolicitudesAusencia } from "pages/vacaciones/componentes/common/mis-solicitudes/hooks";
import MenuAcciones from "./MenuAcciones";
import { SecurityContext } from "context/SecurityContextProvider";

const ListaSolicitudesAusencias = () => {
	const { solicitudesAusencia } = useListarSolicitudesAusencia();
	const { getUsuarioAutenticado } = useContext(SecurityContext);
	const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684" ? true : false;
	const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090" ? true : false;
	const [visibleAprobadores, setVisibleAprobadores] = useState(false);
	const [filaSeleccionada, setFilaSeleccionada] = useState(null);

	const abrirAprobadores = (fila) => {
		setFilaSeleccionada(fila);
		setVisibleAprobadores(true);
	};

	const cerrarAprobadores = () => {
		setVisibleAprobadores(false);
		setFilaSeleccionada(null);
	};

	const columnas = [
		{
			title: (isSDF || isSDFEnergia) ? "Tipo Movimiento":"Tipo Ausencia",
			dataIndex: ["tipo", "descripcion"],
		},
		{
			title: "Fecha Inicio",
			dataIndex: "fechaInicioAusencia",
			render: (text, fila) => { 
				const formato =
						(isSDF || isSDFEnergia)
						? fila?.tipo?.contabilizarHoras
							? "DD/MM/YYYY hh:mm a"
							: "DD/MM/YYYY"
						: "DD/MM/YYYY hh:mm a";
				return moment(fila.fechaInicioAusencia).format(formato);
			}
		},
		{
			title: "Fecha Fin",
			dataIndex: "fechaFinAusencia",
			render: (text, fila) => { 
				const formato =
						(isSDF || isSDFEnergia)
						? fila?.tipo?.contabilizarHoras
							? "DD/MM/YYYY hh:mm a"
							: "DD/MM/YYYY"
						: "DD/MM/YYYY hh:mm a";
				return moment(fila.fechaFinAusencia).format(formato);
			}
		},
		{
			title: "Dias",
			dataIndex: "diasSolicitados",
			align: "center",
		},
		{
			title: "Fecha de Solicitud",
			dataIndex: "fechaHoraSolicitud",
			render: (text, fila) => moment(fila.fechaHoraSolicitud).format("DD/MM/YYYY hh:mm a"),
		},
		{
			title: "Aprobador",
			dataIndex: "aprobador",
			render: (text, fila) => {
				if (isSDF || isSDFEnergia) {
					return <ResumenAprobador fila={fila} onVerDetalle={abrirAprobadores} />;
				}

				if (fila.requiereAprobacionJefeInmediato) {
					return fila.jefeInmediato?.nombreCompleto || "-";
				}
				
				if (
					!fila.requiereAprobacionJefeInmediato &&
					fila.requiereAprobacionOtrosCargos &&
					fila.estado !== "PENDIENTE"
				) {
					return fila.responsableOtrosCargos?.nombreCompleto || "-";
				}
				
				if (fila.permitirAprobacionPrimerFlujo && fila.permitirAprobacionSegundoFlujo && fila.permitirAprobacionTercerFlujo) {
					const primer = fila.responsableAprobacionPrimerNivel?.nombreCompleto || "";
					const segundo = fila.responsableAprobacionSegundoNivel?.nombreCompleto || "";
					const tercer = fila.responsableAprobacionTercerNivel?.nombreCompleto || "";
					
					return [primer, segundo, tercer].filter(Boolean).join(" - ") || "-";
				}
				
				if (fila.permitirAprobacionPrimerFlujo && fila.permitirAprobacionSegundoFlujo) {
					const primer = fila.responsableAprobacionPrimerNivel?.nombreCompleto || "";
					const segundo = fila.responsableAprobacionSegundoNivel?.nombreCompleto || "";
					return segundo ? `${primer} - ${segundo}` : primer || "-";
				}

				if ((fila.permitirAprobacionPrimerFlujo || fila.permitirAprobacionSegundoFlujo) && fila.permitirAprobacionTercerFlujo) {
					//return fila.responsableAprobacionTercerNivel?.nombreCompleto || "-";
					 return [
								fila.responsableAprobacionPrimerNivel?.nombreCompleto,
								fila.responsableAprobacionSegundoNivel?.nombreCompleto,
								fila.responsableAprobacionTercerNivel?.nombreCompleto
							].filter(Boolean).join(" - ") || "-";
				}

				if (fila.permitirAprobacionPrimerFlujo) {
					return fila.responsableAprobacionPrimerNivel?.nombreCompleto || "-";
				}

				if (fila.permitirOtroAprobacionPrimerFlujo) {
					return fila.responsableOtroAprobacionPrimerNivel?.nombreCompleto || "-";
				}
			}
		},
		{
			title: "Estado",
			render: (text, fila) => <EtiquetaEstado fila={fila} />,
		},
		{
			title: "Acciones",
			align: "center",
			render: (text, fila) => <MenuAcciones solicitud={fila} />,
		},
	];

	return (
		<React.Fragment>
			<Table
				rowKey={(value) => value.id}
				columns={columnas}
				dataSource={solicitudesAusencia}
				expandable={{
					expandedRowRender: (fila) => <Comentario registro={fila} />,
					rowExpandable: (fila) =>
					((isSDF || isSDFEnergia) && (
						(fila.comentario !== null && fila.comentario !== undefined && fila.comentario !== "")
						|| fila.informacionAdicional !== null)),
				}}
			/>
			{(isSDF || isSDFEnergia) && (
				<Modal
					open={visibleAprobadores}
					title="Aprobadores"
					onCancel={cerrarAprobadores}
					footer={null}
					width={650}
				>
					<Table
						rowKey={(detalle) => detalle.key}
						columns={columnasAprobadores}
						dataSource={generarDetalleAprobadores(filaSeleccionada)}
						pagination={false}
						size="small"
					/>
				</Modal>
			)}
		</React.Fragment>
	);
};

const columnasAprobadores = [
	{ title: "Rol", dataIndex: "rol", align: "center" },
	{ title: "Responsable", dataIndex: "responsable", align: "center" },
	{ title: "Fecha", dataIndex: "fecha", align: "center" },
	{
		title: "Estado",
		align: "center",
		render: (text, detalle) => (
			<TagEstadoAprobador estado={detalle.estadoAprobacionRaw} estadoSolicitud={detalle.estadoSolicitud} />
		),
	},
];

// Nombre a mostrar para un nivel de aprobación: el responsable ya asignado, o -si aún no se asignó
// a nadie puntual- la lista de candidatos habilitados para atenderlo.
const obtenerNombreResponsableAprobador = (responsable, candidatos) => {
	if (responsable?.nombreCompleto) return responsable.nombreCompleto;
	if (candidatos?.length) return candidatos.map((candidato) => candidato.nombreCompleto).join(", ");
	return "-";
};

const obtenerFechaAprobador = (responsable) => {
	if (responsable?.fechaAprobacion) return "Aprobación: " + moment(responsable.fechaAprobacion).format("DD/MM/YYYY");
	if (responsable?.fechaRechazo) return "Rechazo: " + moment(responsable.fechaRechazo).format("DD/MM/YYYY");
	return "-";
};

// Arma, en orden de flujo, solo los niveles de aprobación que aplican a esta solicitud puntual
// (a diferencia del detalle del admin, aquí se omiten los niveles que no fueron requeridos para
// no mostrarle al colaborador filas irrelevantes con "-").
const generarDetalleAprobadores = (fila) => {
	if (!fila) return [];
	const detalles = [];

	if (fila.permitirAprobacionPrimerFlujo) {
		detalles.push({
			key: "primerFlujo",
			rol: "Aprobación Primer Nivel",
			responsable: obtenerNombreResponsableAprobador(fila.responsableAprobacionPrimerNivel, fila.aprobadoresPrimerNivel),
			fecha: obtenerFechaAprobador(fila.responsableAprobacionPrimerNivel),
			estadoAprobacionRaw: fila.responsableAprobacionPrimerNivel?.estadoAprobacion,
			estadoSolicitud: fila.estado,
		});
	}

	if (fila.permitirOtroAprobacionPrimerFlujo) {
		detalles.push({
			key: "otroPrimerFlujo",
			rol: "Aprobación Primer Nivel",
			responsable: obtenerNombreResponsableAprobador(fila.responsableOtroAprobacionPrimerNivel, fila.otrosAprobadoresPrimerNivel),
			fecha: obtenerFechaAprobador(fila.responsableOtroAprobacionPrimerNivel),
			estadoAprobacionRaw: fila.responsableOtroAprobacionPrimerNivel?.estadoAprobacion,
			estadoSolicitud: fila.estado,
		});
	}

	if (fila.permitirAprobacionSegundoFlujo) {
		detalles.push({
			key: "segundoFlujo",
			rol: "Aprobación Segundo Nivel",
			responsable: obtenerNombreResponsableAprobador(fila.responsableAprobacionSegundoNivel, fila.aprobadoresSegundoNivel),
			fecha: obtenerFechaAprobador(fila.responsableAprobacionSegundoNivel),
			estadoAprobacionRaw: fila.responsableAprobacionSegundoNivel?.estadoAprobacion,
			estadoSolicitud: fila.estado,
		});
	}

	if (fila.permitirAprobacionTercerFlujo) {
		detalles.push({
			key: "tercerFlujo",
			rol: "Aprobación Tercer Nivel",
			responsable: obtenerNombreResponsableAprobador(fila.responsableAprobacionTercerNivel, fila.aprobadoresTercerNivel),
			fecha: obtenerFechaAprobador(fila.responsableAprobacionTercerNivel),
			estadoAprobacionRaw: fila.responsableAprobacionTercerNivel?.estadoAprobacion,
			estadoSolicitud: fila.estado,
		});
	}

	if (fila.requiereAprobacionJefeInmediato) {
		detalles.push({
			key: "jefe",
			rol: "Jefe Inmediato",
			responsable: fila.jefeInmediato?.nombreCompleto || "-",
			fecha: obtenerFechaAprobador(fila.jefeInmediato),
			estadoAprobacionRaw: fila.jefeInmediato?.estadoAprobacion,
			estadoSolicitud: fila.estado,
		});
	}

	if (fila.requiereAprobacionOtrosCargos) {
		detalles.push({
			key: "otros",
			rol: "Otros Cargos",
			responsable: fila.responsableOtrosCargos?.nombreCompleto || "-",
			fecha: obtenerFechaAprobador(fila.responsableOtrosCargos),
			estadoAprobacionRaw: fila.responsableOtrosCargos?.estadoAprobacion,
			estadoSolicitud: fila.estado,
		});
	}

	if (fila.requiereAprobacionGestionHumana) {
		detalles.push({
			key: "gestion",
			rol: "Gestión Humana",
			responsable: fila.responsableGestionHumana?.nombreCompleto || "-",
			fecha: obtenerFechaAprobador(fila.responsableGestionHumana),
			estadoAprobacionRaw: fila.responsableGestionHumana?.estadoAprobacion,
			estadoSolicitud: fila.estado,
		});
	}

	return detalles;
};

// De todos los niveles configurados, cuál mostrar como resumen en la columna "Aprobador":
// el que rechazó, si la solicitud fue rechazada; el primero pendiente, si sigue en curso;
// o el último nivel (ya resuelto) si la solicitud terminó aprobada/cancelada.
const obtenerResumenAprobador = (fila, detalles) => {
	if (!detalles.length) return null;

	if (fila.estado === "RECHAZADO") {
		return detalles.find((detalle) => detalle.estadoAprobacionRaw === "RECHAZADO") || detalles[detalles.length - 1];
	}

	const pendiente = detalles.find((detalle) => detalle.estadoAprobacionRaw === "PENDIENTE");
	if (pendiente) return pendiente;

	return detalles[detalles.length - 1];
};

const TagEstadoAprobador = ({ estado, estadoSolicitud }) => {
	if (!estado) return null;

	if (estado === "PENDIENTE" && estadoSolicitud === "CANCELADO") {
		return <Tag color="purple" className="milegajo-ant-tag">CANCELADO</Tag>;
	}
	if (estado === "PENDIENTE") {
		return <Tag color="gold" className="milegajo-ant-tag">PENDIENTE</Tag>;
	}
	if (estado === "APROBADO") {
		return <Tag color="green" className="milegajo-ant-tag">APROBADO</Tag>;
	}
	if (estado === "RECHAZADO") {
		return <Tag color="red" className="milegajo-ant-tag">RECHAZADO</Tag>;
	}
	return null;
};

// Columna "Aprobador" para SDF/SDFEnergia: solo el nombre del responsable al que le toca actuar
// ahora (o el que resolvió la solicitud) -el estado ya lo dice la columna "Estado", no hace falta
// repetirlo aquí-, y un acceso a "Ver aprobadores" solo cuando hay más de un nivel configurado
// -si es uno solo, ya está todo visible en la propia celda-.
const ResumenAprobador = ({ fila, onVerDetalle }) => {
	const detalles = generarDetalleAprobadores(fila);
	const resumen = obtenerResumenAprobador(fila, detalles);

	return (
		<Space direction="vertical" size={2}>
			<span>{resumen?.responsable || "-"}</span>
			{detalles.length > 1 && (
				<Button
					type="link"
					size="small"
					icon={<TeamOutlined />}
					style={{ padding: 0, height: "auto" }}
					onClick={() => onVerDetalle(fila)}
				>
					Ver aprobadores
				</Button>
			)}
		</Space>
	);
};

const EtiquetaEstado = ({ fila }) => {
	const tagStyle = {
		borderRadius: "15px",
	};

	const color = getColor(fila.estado);

	if (fila.estado === "RECHAZADO") {

		if (fila.jefeInmediato != null && fila.jefeInmediato.motivoRechazo != null) {
			const content = (
				<div style={{ width: 140 }}>
					<p>
						<strong>Motivo rechazo: </strong>
						{fila.jefeInmediato.motivoRechazo}
					</p>
				</div>
			);

			return (
				<div style={{ width: 140 }}>
					<Popover
						title={null}
						content={content}
						trigger="click"
						placement="bottomRight"
					// onOpenChange={onOpenChange}
					>
						<Space>
							<Tag color="red" className="milegajo-ant-tag" style={{ cursor: "pointer", ...tagStyle }}>
								<SearchOutlined /> RECHAZADO
							</Tag>
						</Space>
					</Popover>
				</div>
			);
		} else {
			return (
				<Tag color={color} className="milegajo-ant-tag">
					{fila.estado}
				</Tag>
			);
		}
		
	}
	// Antes, mientras la solicitud requería convenio de fraccionamiento/adelanto, quedaba en estado
	// PENDIENTE hasta que el colaborador entraba a firmarlo manualmente desde su portal, y este mensaje le
	// avisaba de eso. Ahora (SDF/SDF Energía) el convenio se genera y firma automático al crear la
	// solicitud: el estado pasa a APROBADO al aprobarse el último nivel sin esperar ninguna firma del
	// colaborador, así que este aviso ya no aplica -si sigue en PENDIENTE es porque falta la aprobación de
	// otro nivel, no porque el colaborador tenga algo pendiente de firmar-.

	return (
		<Tag color={color} className="milegajo-ant-tag">
			{fila.estado}
		</Tag>
	);
};

const getColor = (estado) => {
	if (estado === "APROBADO") {
		return "green";
	} else if (estado === "RECHAZADO") {
		return "red";
	} else if (estado === "PENDIENTE") {
		return "gold";
	} else {
		return "magenta";
	}
};

const Comentario = ({ registro }) => {
	const { getUsuarioAutenticado } = useContext(SecurityContext);
	const isMaccaferriDePeru = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20418710757" ? true : false;
	const isMaccaferriConstruction = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20521749378" ? true : false;
	const isAceros = getUsuarioAutenticado().empresaSeleccionada.razonSocial.toUpperCase().includes("ACEROS CHILCA SAC")
		? true
		: false;
	const isSDF = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20330791684" ? true : false;
	const isSDFEnergia = getUsuarioAutenticado().empresaSeleccionada?.ruc === "20512436090" ? true : false;

	return (
		<React.Fragment>
			{(isSDF || isSDFEnergia) && (
				<>
					{registro.informacionAdicional ? (
						<div style={{ marginLeft: 50 }}>
							{Object.entries(registro.informacionAdicional).map(([key, value], index) => (
								<p key={index}>
									<strong>{key.replace(/([A-Z])/g, " $1").toUpperCase()}: </strong>
									{typeof value === "boolean"
										? value
											? "Sí"
											: "No"
										: typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T/) && moment(value).isValid()// Comprueba si es una fecha en formato ISO
										? moment(value).format("DD/MM/YYYY")
										: value}
								</p>
							))}
							<p>
								<strong>Comentario: </strong> {registro.comentario}
							</p>
						</div>
					) : (
						<p>
							<strong>Comentario: </strong> {registro.comentario}
						</p>
					)}
				</>
			)}
		</React.Fragment>
	);
};

export default ListaSolicitudesAusencias;
